import type {
  AnalyticsSourceName,
  ApiResult,
  OperationName,
} from 'features/feeBasedInstantTransfer/helpers/analyticsFormatters';
import useTransferAnalytics from 'features/feeBasedInstantTransfer/hooks/useTransferAnalytics';
import { useEffect, useRef } from 'react';
import useEvent from 'shared/hooks/useEvent/useEvent';
import type { UseQueryState } from 'urql';

/**
 * Logs one Request Completed each time a query round trip settles, including
 * re-executions. A request cancelled by pausing the query never reached an
 * outcome, so it is not logged.
 */
export default function useOperationAnalytics(
  sourceName: AnalyticsSourceName,
  operationName: OperationName,
  { fetching, error, extensions }: UseQueryState,
  result: ApiResult | null | undefined,
  isPaused: boolean,
) {
  const { logRequestCompleted } = useTransferAnalytics(sourceName);
  const isAwaitingResultRef = useRef(false);
  const logSettledRequest = useEvent(() => {
    logRequestCompleted(operationName, { result, error, extensions });
  });

  useEffect(() => {
    if (fetching) {
      isAwaitingResultRef.current = true;
    } else if (isAwaitingResultRef.current) {
      isAwaitingResultRef.current = false;
      if (!isPaused) {
        logSettledRequest();
      }
    }
  }, [fetching, isPaused, logSettledRequest]);
}
